/**
 * TOSS Web Application — a choice chip
 *
 * One segment of a single-choice row: «Все / Важные / Непрочитанные» over the notifications (the
 * bell panel and the full page, 24.09.2026), «Все / Новая / В работе / …» over the portal's
 * applications list, «Все / Акты / Отчёты» over its documents registry. Born in the portal as
 * `ClientFilterChip` after three screens had drawn the same button three times; lifted here when
 * the internal side needed it too, and the portal name stays as an alias.
 *
 * Not the audit log's `FilterChip` — that one is a removable summary of an active filter, this one
 * IS the filter.
 *
 * **No `touch-target`, deliberately.** That utility forces `min-width: 48px` as well as
 * `min-height`, and on a short word both apply: the chip inflates into a circle and the row reads
 * as buttons of unrelated sizes. A chip in a row of its own stays 32 px (frontend-ui rules).
 *
 * Single choice by construction: `aria-pressed` marks the active one, and the row is a plain flex
 * line rather than a `radiogroup`, because these chips are shortcuts into a filter — not a form
 * control anybody submits.
 */

import type { ReactNode } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface ChoiceChipProps {
  isActive: boolean;
  onClick: () => void;
  children: ReactNode;
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export function ChoiceChip({
  isActive,
  onClick,
  children,
  className = '',
}: ChoiceChipProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={`
        h-8 px-3 inline-flex items-center rounded-full text-sm font-medium
        whitespace-nowrap transition-colors border
        focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)]
        ${
          isActive
            ? 'bg-[var(--color-primary-500)] text-[var(--color-text-on-primary)] border-transparent'
            : 'bg-[var(--color-bg-card)] text-[var(--color-text-secondary)] border-[var(--color-border)] hover:bg-[var(--color-bg-secondary)]'
        }
        ${className}
      `}
    >
      {children}
    </button>
  );
}

export default ChoiceChip;
